import { useEffect, useState } from 'react';
import { motivoLegivel } from '@/services/erroLegivel';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Button } from '@/components/atoms';
import { brandService, DEFAULT_BRAND, type Brand } from '@/services/brand';
import { usePlatformAccess } from '@/services/platform';
import { useBrand } from '@/store/useBrand';

import styles from './ConfigMarca.module.css';

// A marca é POR CLIENTE. `''` = marca da CASA: a neutra que aparece antes do
// login num host compartilhado e numa instalação nova — não é "nenhuma", é um
// escopo real e editável.
const CASA = '';

export function ConfigMarca() {
  const { t } = useTranslation([
    'common',
    'nav',
    'board',
    'articles',
    'analytics',
    'feeddrive',
    'config',
    'strategist',
  ]);
  const current = useBrand((s) => s.brand);
  const applyBrand = useBrand((s) => s.applyBrand);
  const { clients } = usePlatformAccess();
  const [escopo, setEscopo] = useState<string>(CASA);
  const [form, setForm] = useState<Brand>(current);
  const [ok, setOk] = useState(false);

  // Lê o que está GRAVADO no escopo escolhido — não o que está aplicado na
  // sessão de quem edita, senão a tela mostraria a marca errada.
  const gravada = useQuery({
    queryKey: ['brand-scope', escopo],
    queryFn: () => (escopo ? brandService.getFor(escopo) : brandService.get()),
  });

  useEffect(() => {
    if (gravada.data) {
      setForm(gravada.data);
      setOk(false);
    }
  }, [gravada.data]);

  const set = <K extends keyof Brand>(k: K, v: Brand[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setOk(false);
  };

  const save = useMutation({
    mutationFn: () => brandService.update(form, escopo || undefined),
    onSuccess: (brand) => {
      // Só aplica na sessão em curso se foi a marca da CASA que mudou; aplicar
      // a de um cliente aqui pintaria a tela da casa com a marca dele.
      if (!escopo) applyBrand(brand);
      setOk(true);
    },
  });

  const onLogoFile = (file: File | undefined, field: 'logo_url' | 'favicon') => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set(field, String(reader.result)); // data URI
    reader.readAsDataURL(file);
  };

  const previewColor = form.brand_color || 'var(--nexus-brand)';

  return (
    <section className={styles.root} aria-label={t('config:brand.title', 'Marca')}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Marca</h1>
          <p className={styles.subtitle}>
            White-label em runtime, por cliente. O que você definir aqui aplica na hora — sem
            rebuild, sem código. Cliente novo = escolher o cliente e preencher esta tela.
          </p>
        </div>
        <div className={styles.headerActions}>
          <select
            className={styles.input}
            value={escopo}
            onChange={(e) => setEscopo(e.target.value)}
            aria-label="Marca de qual cliente"
          >
            <option value={CASA}>Marca da casa (pré-login)</option>
            {clients.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name ?? c.slug}
              </option>
            ))}
          </select>
          <Button variant="ghost" onClick={() => setForm({ ...DEFAULT_BRAND })}>
            Restaurar padrão
          </Button>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Salvar
          </Button>
        </div>
      </header>

      {ok && <p className={styles.ok}>Marca aplicada.</p>}
      {/* A mensagem era fixa: "Falha ao salvar (precisa ser admin)" — dita a um
          usuario que E admin, escondendo o motivo real (na epoca, um 404 de
          gate no backend). Agora a tela repete o motivo do servidor. */}
      {save.isError && <p className={styles.err}>{motivoLegivel(save.error)}</p>}

      <div className={styles.grid}>
        <div className={styles.formCol}>
          <Field label="Nome" hint="Aparece no header e no login">
            <input
              className={styles.input}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              maxLength={40}
            />
          </Field>
          <Field label="Sufixo" hint="Ex.: 'OS', 'Desk' (opcional)">
            <input
              className={styles.input}
              value={form.sub}
              onChange={(e) => set('sub', e.target.value)}
              maxLength={12}
            />
          </Field>
          <Field label="Tagline" hint="Linha sob o nome no login">
            <input
              className={styles.input}
              value={form.tagline}
              onChange={(e) => set('tagline', e.target.value)}
              maxLength={120}
            />
          </Field>
          <Field label="Cor da marca" hint="Vazio = azul padrão">
            <div className={styles.colorRow}>
              {/* cor da marca do CLIENTE — e dado editavel, nao cor da interface */}
              <input
                type="color"
                className={styles.colorPick}
                value={form.brand_color || '#3b82f6'}
                onChange={(e) => set('brand_color', e.target.value)}
              />
              <input
                className={styles.input}
                placeholder="#RRGGBB"
                value={form.brand_color}
                onChange={(e) => set('brand_color', e.target.value)}
                maxLength={9}
              />
              {form.brand_color && (
                <button
                  type="button"
                  className={styles.clearBtn}
                  onClick={() => set('brand_color', '')}
                >
                  limpar
                </button>
              )}
            </div>
          </Field>
          <Field label="Logo" hint="PNG/SVG. Vazio = wordmark do nome">
            <div className={styles.fileRow}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => onLogoFile(e.target.files?.[0], 'logo_url')}
              />
              {form.logo_url && (
                <button
                  type="button"
                  className={styles.clearBtn}
                  onClick={() => set('logo_url', '')}
                >
                  remover
                </button>
              )}
            </div>
          </Field>
          <Field label="Favicon" hint="Opcional. Vazio = padrão">
            <div className={styles.fileRow}>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => onLogoFile(e.target.files?.[0], 'favicon')}
              />
              {form.favicon && (
                <button
                  type="button"
                  className={styles.clearBtn}
                  onClick={() => set('favicon', '')}
                >
                  remover
                </button>
              )}
            </div>
          </Field>
        </div>

        {/* Preview ao vivo */}
        <div className={styles.previewCol}>
          <p className={styles.previewLabel}>Pré-visualização</p>
          <div className={styles.previewCard}>
            <div className={styles.previewHeader}>
              {form.logo_url ? (
                <img className={styles.previewLogo} src={form.logo_url} alt="" />
              ) : (
                <span className={styles.previewMark} style={{ background: previewColor }} />
              )}
              <span className={styles.previewName}>{form.name || 'NOME'}</span>
              <span className={styles.previewSub}>{form.sub}</span>
            </div>
            <p className={styles.previewTag}>{form.tagline}</p>
            <button
              className={styles.previewBtn}
              style={{ background: previewColor }}
              type="button"
            >
              Botão de marca
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
      {hint && <span className={styles.fieldHint}>{hint}</span>}
    </label>
  );
}
